import _ from 'lodash';
import { PropsWithChildren, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { Button, Callout, Modal, Popover, Portal } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import useViewer from 'packages/kakapo-ui-application/hooks/useViewer';

import VoiceMemoButton from './VoiceMemoButton';
import VoiceMemoOverlay from './VoiceMemoOverlay';
import VoiceMemoPlayback from './VoiceMemoPlayback';
import VoiceMemoTransition from './VoiceMemoTransition';

type VoiceMemoDraft = { blob: Blob; samples: number[]; sampleRate: number };

export default function VoiceMemoTarget(
	props: PropsWithChildren<{
		voiceMemo?: VoiceMemoDraft | graphql.VoiceMemo;
		onChange: (voiceMemo?: VoiceMemoDraft) => void;
	}>,
) {
	const enabled = !!useViewer();
	const { t } = useTranslation();
	const ref = useRef<HTMLDivElement | null>(null);
	const [active, setActive] = useState(false);
	const [error, setError] = useState(false);
	const [bounds, setBounds] = useState<
		Record<'top' | 'left' | 'width' | 'height', number> | undefined
	>();
	const [, setRandom] = useState(Math.random());

	const lastVoiceMemo = useRef(props.voiceMemo);

	if (props.voiceMemo) {
		lastVoiceMemo.current = props.voiceMemo;
	}

	return (
		<>
			<div
				ref={ref}
				style={{
					position: 'relative',
				}}
			>
				{enabled && (
					<div
						style={{
							position: 'absolute',
							left: -40,
							opacity: lastVoiceMemo.current || bounds ? 0 : 1,
						}}
					>
						<Popover.Target
							visible={error}
							contents={
								<Box>
									<Box p={3} maxWidth={400}>
										<Callout color="danger">{t('voiceMemo.error.message')}</Callout>
									</Box>
									<Modal.Footer>
										<Button
											type="primary"
											small
											title={t('voiceMemo.error.dismiss')}
											onClick={() => setError(false)}
										/>
									</Modal.Footer>
								</Box>
							}
							onDismiss={() => setError(false)}
						>
							<VoiceMemoButton
								onClick={() => {
									setError(false);
									setBounds(
										_.pick(ref.current!.getBoundingClientRect(), [
											'top',
											'left',
											'width',
											'height',
										]),
									);
								}}
							/>
						</Popover.Target>
					</div>
				)}
				<div
					style={{
						opacity: active || props.voiceMemo ? 0 : 1,
						transformOrigin: '-24px 16px',
						transform: active || props.voiceMemo ? 'scale(0, 0)' : 'none',
						transition: '1s cubic-bezier(0, 1, 0, 1) all',
					}}
				>
					{props.children}
				</div>
				{lastVoiceMemo.current && (
					<div style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%' }}>
						<VoiceMemoTransition status={props.voiceMemo ? 'expanded' : 'collapsed'}>
							<VoiceMemoPlayback
								fromRecording
								source={
									'blob' in lastVoiceMemo.current
										? lastVoiceMemo.current.blob
										: lastVoiceMemo.current.asset.source.url
								}
								samples={lastVoiceMemo.current.samples}
								onCancel={() => {
									props.onChange();

									setActive(false);

									setTimeout(() => {
										lastVoiceMemo.current = undefined;
										setRandom(Math.random());
									}, 1000);
								}}
							/>
						</VoiceMemoTransition>
					</div>
				)}
			</div>
			{bounds && !props.voiceMemo && (
				<Portal>
					<Box style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }} />
					<Box style={{ position: 'absolute', ...bounds, top: bounds.top + window.scrollY }}>
						<VoiceMemoOverlay
							onStart={() => setActive(true)}
							onError={() => {
								setError(true);
								setBounds(undefined);
							}}
							onFinish={(voiceMemo) => {
								props.onChange(voiceMemo);
								setBounds(undefined);
							}}
						/>
					</Box>
				</Portal>
			)}
		</>
	);
}
